import { NPaymentError } from '../errors.js';

/**
 * v0.21 — Stellar brand-stablecoin registry.
 *
 * Single source of truth for the brand stables that coexist on Stellar today.
 * Curated set: USDC + EURC + MGUSD (MoneyGram, launched 2026-06-02). RLUSD and
 * PYUSD are deferred until verified Stellar issuers exist (avoids placeholder
 * drift across modules).
 *
 * Pattern matches v0.17 `goat.usdcOverride` — unverified issuer addresses ship
 * as placeholders behind an env override, never blocking the SDK at import time.
 *
 * Design (SOLID):
 *   - SRP — this module owns asset metadata + decimal helpers, nothing else.
 *   - OCP — additional brand stables added via PR (env override at runtime).
 *   - DIP — downstream (`session`, `asset-router`, `stellar-anchor`, `kya`)
 *           depend on `getStellarAsset` not on hard-coded issuer strings.
 */

export type StellarAssetSymbol = 'USDC' | 'EURC' | 'MGUSD';

export interface StellarAsset {
  /** Canonical Stellar asset code (≤12 chars). */
  code: StellarAssetSymbol;
  /**
   * Classic Stellar issuer address (G..., 56 chars) — required for assets that
   * support Horizon path payments (`/paths/strict-receive`) via `asset-router`.
   * Absent for SAC-only assets (see `sacAddress`); callers MUST check
   * `isClassicAsset()` before reading this field for path-payment routing.
   */
  issuer?: string;
  /**
   * Soroban Stellar Asset Contract address (C..., 56 chars) — present when the
   * asset's only on-chain identity is a SAC (e.g. MGUSD, which has no classic
   * issuer at all: it is minted/burned by the M0 "Minter Gateway" Soroban
   * contract directly against the SAC). Horizon's classic path-payment API
   * cannot route these; callers must use Soroban SEP-41 `transfer()` directly.
   */
  sacAddress?: string;
  /** Display symbol (= code in v0.21). */
  symbol: string;
  /** Stellar canonical decimals = 7. Carried explicitly for parse/format helpers. */
  decimals: 7;
  /** Brand owner (for routing heuristics + display). */
  brand: 'circle' | 'circle-eur' | 'moneygram';
  /** Underlying issuance infra (modular-stack thesis). */
  infra: 'circle' | 'm0';
  /** Whether the on-chain address (issuer or sacAddress) is verified or a placeholder pending publication. */
  verified: boolean;
  /**
   * MGUSD-specific: the SAC runs `AUTH_REQUIRED` with a mandatory allowlist —
   * an address must be `unblock_user`-whitelisted by the issuer (Bridge)
   * before it can send OR receive tokens. Classic Circle assets (USDC/EURC)
   * have no such gate once a trustline exists. Callers that build transfer
   * flows generically MUST check this flag — a payment to/from an
   * un-whitelisted address is not just declined, it needs a whitelist step
   * neither n-payment nor the counterparty can skip.
   */
  requiresAllowlist: boolean;
  /** Optional Anchor Directory hint for SEP-24/31 lookup in `offramp/stellar-anchor`. */
  anchorDirectoryEntry?: { home_domain: string; name: string };
  /** Env-var key for runtime issuer/SAC-address rebinding (verified=true override path). */
  envOverride?: string;
}

/** True when the asset has a classic issuer and can be routed via Horizon path payments. */
export function isClassicAsset(asset: StellarAsset): asset is StellarAsset & { issuer: string } {
  return typeof asset.issuer === 'string' && asset.issuer.length > 0;
}

/**
 * Stellar's "null account" — 56-char base32 G-prefix shape, all-zero ed25519 pubkey.
 * Used as a placeholder until a real classic issuer is published for an asset that
 * needs one. MGUSD does not use this — it is SAC-only, see `MGUSD_SAC_PLACEHOLDER`.
 */
export const MGUSD_ISSUER_PLACEHOLDER =
  'GAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWHATTHEHELLOMGUSD'.slice(0, 56);

/**
 * Placeholder Soroban contract-address shape (56-char base32 C-prefix, all-zero).
 * MGUSD's real mainnet SAC is a Soroban contract, not a classic issuer account —
 * verify against https://docs.m0.org/resources/addresses/mgusd-platform before
 * shipping a non-testnet build. Set STELLAR_MGUSD_SAC to override at runtime.
 */
export const MGUSD_SAC_PLACEHOLDER =
  'CAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWHATTHEHELLOMGUSD'.slice(0, 56);

/** Module-load-time map; env overrides re-resolved at call time in `getStellarAsset`. */
export const STELLAR_ASSETS: Record<StellarAssetSymbol, StellarAsset> = {
  USDC: {
    code: 'USDC',
    issuer: 'GA5ZSEJYB37JRC5AVCIA5MOP4RHTM335X2KGX3IHOJAPP5RE34K4KZVN',
    symbol: 'USDC',
    decimals: 7,
    brand: 'circle',
    infra: 'circle',
    verified: true,
    requiresAllowlist: false,
    anchorDirectoryEntry: { home_domain: 'circle.com', name: 'Circle' },
  },
  EURC: {
    code: 'EURC',
    issuer: 'GDHU6WRG4IEQXM5NZ4BMPKOXHW76MZM4Y2IEMFDVXBSDP6SJY4ITNPP2',
    symbol: 'EURC',
    decimals: 7,
    brand: 'circle-eur',
    infra: 'circle',
    verified: true,
    requiresAllowlist: false,
    anchorDirectoryEntry: { home_domain: 'circle.com', name: 'Circle EURC' },
  },
  MGUSD: {
    code: 'MGUSD',
    // No classic issuer — MGUSD is SAC-only (see `sacAddress` + isClassicAsset()).
    sacAddress: MGUSD_SAC_PLACEHOLDER,
    symbol: 'MGUSD',
    decimals: 7,
    brand: 'moneygram',
    infra: 'm0',
    verified: false,
    requiresAllowlist: true,
    anchorDirectoryEntry: { home_domain: 'moneygram.com', name: 'MoneyGram' },
    envOverride: 'STELLAR_MGUSD_SAC',
  },
};

export const STELLAR_ASSET_SYMBOLS = Object.keys(STELLAR_ASSETS) as StellarAssetSymbol[];

/**
 * Resolve an asset by symbol (case-insensitive). Re-reads env overrides at call time
 * so test isolation and runtime rebinding both work (no module-cache pinning).
 */
export function getStellarAsset(symbol: string): StellarAsset {
  const upper = symbol.toUpperCase() as StellarAssetSymbol;
  const asset = STELLAR_ASSETS[upper];
  if (!asset) {
    throw new NPaymentError(
      `Unknown Stellar asset: ${symbol}`,
      'STELLAR_ASSET_UNKNOWN',
      `Known assets: ${STELLAR_ASSET_SYMBOLS.join(', ')}`,
    );
  }
  if (asset.envOverride) {
    const override = process.env[asset.envOverride];
    if (override && override.length > 0) {
      // Write to whichever address field this asset actually uses — never
      // assume "issuer"; MGUSD's override is a SAC contract address (C...),
      // not a classic issuer (G...).
      const field = asset.sacAddress !== undefined ? 'sacAddress' : 'issuer';
      return { ...asset, [field]: override, verified: true };
    }
  }
  return asset;
}

/** Warn-once helper for placeholder addresses — operators see exactly one line per asset per process. */
const _warned = new Set<string>();
export function assertVerifiedIssuer(asset: StellarAsset): void {
  if (asset.verified) return;
  if (_warned.has(asset.code)) return;
  _warned.add(asset.code);
  const kind = asset.sacAddress !== undefined ? 'SAC address' : 'issuer';
  // Use console.warn so test runners can capture it without throwing.
  // eslint-disable-next-line no-console
  console.warn(
    `[n-payment] STELLAR_ASSET_PLACEHOLDER_ISSUER: ${asset.code} ${kind} is a placeholder. ` +
      `Set ${asset.envOverride ?? `STELLAR_${asset.code}_ISSUER`} env var with the verified ${kind}.`,
  );
}

/**
 * Parse a decimal display string (e.g. `'1.5'`) into base-unit bigint (e.g. `15_000_000n` for 7 decimals).
 * Truncates excess fractional digits (does not round) — matches v0.20 `parseSpace` semantics.
 */
export function parseStellarAsset(amount: string | number, symbol: string): bigint {
  const asset = getStellarAsset(symbol);
  const str = String(amount);
  if (!/^\d+(\.\d+)?$/.test(str)) {
    throw new NPaymentError(
      `Invalid amount for ${symbol}: ${str}`,
      'STELLAR_ASSET_INVALID_AMOUNT',
      `Expected decimal string with up to ${asset.decimals} decimals (e.g. '1.5').`,
    );
  }
  const [whole, frac = ''] = str.split('.');
  const padded = (frac + '0'.repeat(asset.decimals)).slice(0, asset.decimals);
  return BigInt(whole) * 10n ** BigInt(asset.decimals) + BigInt(padded || '0');
}

/**
 * Format a base-unit bigint into a decimal display string. `displayDecimals` lets callers
 * truncate trailing zeros (e.g. `'1.50'` instead of `'1.5000000'`). Defaults to asset decimals.
 */
export function formatStellarAsset(
  amount: bigint,
  symbol: string,
  displayDecimals?: number,
): string {
  const asset = getStellarAsset(symbol);
  const dd = displayDecimals ?? asset.decimals;
  const divisor = 10n ** BigInt(asset.decimals);
  const whole = amount / divisor;
  if (dd === 0) return whole.toString();
  const frac = amount % divisor;
  const fracStr = frac.toString().padStart(asset.decimals, '0').slice(0, dd);
  return `${whole}.${fracStr}`;
}
